'use client';

import {
  createContext, ReactNode, useCallback, useContext, useMemo, useState,
} from 'react';

interface MissionControlBestieChatPrompt {
  id: string;
  text: string;
}

interface MissionControlBestieChatContextValue {
  isChatOpen: boolean;
  promptRequest: MissionControlBestieChatPrompt | null;
  openChat: (prompt: string) => void;
  closeChat: () => void;
}

const MissionControlBestieChatContext = createContext<MissionControlBestieChatContextValue | undefined>(undefined);

interface MissionControlBestieChatProviderProps {
  children: ReactNode;
}

export const MissionControlBestieChatProvider = ({ children }: MissionControlBestieChatProviderProps) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [promptRequest, setPromptRequest] = useState<MissionControlBestieChatPrompt | null>(null);

  const openChat = useCallback((prompt: string) => {
    setPromptRequest({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      text: prompt,
    });
    setIsChatOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
    setPromptRequest(null);
  }, []);

  const value = useMemo(() => ({
    isChatOpen,
    promptRequest,
    openChat,
    closeChat,
  }), [isChatOpen, promptRequest, openChat, closeChat]);

  return (
    <MissionControlBestieChatContext.Provider value={value}>
      {children}
    </MissionControlBestieChatContext.Provider>
  );
};

export const useMissionControlBestieChat = () => {
  const context = useContext(MissionControlBestieChatContext);

  if (!context) {
    throw new Error('useMissionControlBestieChat must be used within MissionControlBestieChatProvider');
  }

  return context;
};
